import { AudioEngine } from '../audio/engine';
import { GAME_CONFIG } from '../config/gameConfig';
import type { GameState } from '../state/types';
import type { SceneContext } from '../scene/setup';
import { createFloatingIndicator } from '../ui/floatingLabel';

export function syncAutopilotUi(state: GameState): void {
  const banner = document.getElementById('autopilot-banner');
  const status = document.getElementById('autopilot-status');
  const title = document.getElementById('autopilot-title');
  const ping = document.getElementById('autopilot-ping');
  const dot = document.getElementById('autopilot-dot');
  const toggle = document.getElementById('autopilot-toggle') as HTMLInputElement | null;
  const speedPct = Math.round(GAME_CONFIG.autopilot.speedMultiplier * 100);
  const on = state.autoPilotActive;

  if (toggle) {
    toggle.checked = on;
  }

  if (banner) {
    banner.classList.toggle('border-l-emerald-500', on);
    banner.classList.toggle('border-l-amber-500', !on);
  }

  if (ping) {
    ping.classList.toggle('bg-emerald-400', on);
    ping.classList.toggle('bg-amber-400', !on);
  }

  if (dot) {
    dot.classList.toggle('bg-emerald-500', on);
    dot.classList.toggle('bg-amber-500', !on);
  }

  if (title) {
    title.textContent = on ? 'Robotpilóta Aktív' : 'Robotpilóta Kikapcsolva';
    title.classList.toggle('text-emerald-300', on);
    title.classList.toggle('text-amber-300', !on);
  }

  if (status) {
    status.textContent = on
      ? `Egységek automatikusan munkába állnak [${speedPct}% sebesség]`
      : 'Kapcsold be a sidebarban a robotpilótát.';
  }
}

export function setAutoPilotEnabled(state: GameState, enabled: boolean): void {
  if (state.autoPilotActive === enabled) {
    syncAutopilotUi(state);
    return;
  }

  state.autoPilotActive = enabled;
  syncAutopilotUi(state);

  if (enabled) {
    AudioEngine.playBeep(880, 'sine', 0.2);
  }
}

export function runAutoPilotDecisions(state: GameState, ctx: SceneContext): void {
  if (!state.autoPilotActive) return;

  if (state.drone.state === 'Idle') {
    // Prefer fields with no NDVI; among those, highest infection first.
    const needsScan = state.fields
      .filter((f) => !f.dead && f.ndviRemaining <= 0)
      .sort((a, b) => b.infection - a.infection);
    const target = needsScan[0];

    if (target) {
      state.drone.state = 'Moving';
      state.drone.targetFieldId = target.id;
      state.drone.task = 'Scan';
      state.drone.progress = 0;
      createFloatingIndicator(ctx, '🤖 Auto-Pilot: Drón indítva', target.x, target.z);
    }
  }

  if (state.truck.state === 'Idle') {
    const knownInfectedFields = state.fields.filter(
      (f) =>
        f.ndviRemaining > 0 && f.infection > GAME_CONFIG.autopilot.treatInfectionMin && !f.dead,
    );
    const sortedByInfection = knownInfectedFields.sort((a, b) => b.infection - a.infection);
    const worstInfected = sortedByInfection[0];

    if (worstInfected) {
      state.truck.state = 'Moving';
      state.truck.targetFieldId = worstInfected.id;
      state.truck.task = 'Treat';
      state.truck.progress = 0;
      createFloatingIndicator(
        ctx,
        '🤖 Auto-Pilot: Pickup indítva',
        worstInfected.x,
        worstInfected.z,
      );
    }
  }
}
